import { useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LocationOn, MyLocation, Close } from '@mui/icons-material';
import { CityContext } from '../../ContextApi/cityContext';
import utilsCities from '../../_Utils/utilsCities';
import './CityPicker.css';

// בורר העיר: מסך חוסם כשיש ערים ועוד לא נבחרה עיר (אין ברירת מחדל), או חלון כשפותחים
// מהכותרת / התפריט ("החלף עיר"). "שתף מיקום" - זיהוי דרך השרת; כל כישלון (סירוב, timeout,
// מחוץ לכל העיירות) -> הודעה ורשימת הערים לבחירה ידנית.

// עמודים שלא חוסמים (חזרה מתשלום, הסרה מרשימת תפוצה, עמוד מקום - הוא בוחר את המקום לבד)
const NO_GATE_PATHS = ['/thankU', '/ErrorPay', '/unsubscribe', '/city/'];
// זחלנים / רינדור מקדים - לא חוסמים (SEO)
const isBot = () => typeof navigator !== 'undefined' &&
    /HeadlessChrome|bot|crawler|spider|Lighthouse|rendertron/i.test(navigator.userAgent || '');

const FAIL_MSG = {
    denied: 'לא ניתנה הרשאה למיקום. בחרו עיר מהרשימה:',
    timeout: 'לא הצלחנו לקבל את המיקום בזמן. בחרו עיר מהרשימה:',
    unavailable: 'לא הצלחנו לקבל את המיקום. בחרו עיר מהרשימה:',
    unsupported: 'הדפדפן לא מאפשר שיתוף מיקום. בחרו עיר מהרשימה:',
    outside: 'נראה שאתם מחוץ לאזורי השירות שלנו. בחרו עיר מהרשימה:',
    error: 'משהו השתבש בזיהוי העיר. בחרו עיר מהרשימה:',
};

// מגבלה כוללת: גם אם המשתמש לא עונה לבקשת ההרשאה - לא מחכים לנצח
const withTimeout = (p, ms) => Promise.race([
    p, new Promise((_, rej) => setTimeout(() => rej({ reason: 'timeout' }), ms)),
]);

// מקום עם כמה ערים (מרחב) - אחרי הבחירה בו נשאלים באיזו עיר בתוכו (myCity, ContextApi/cityContext.js)
const hasManyCities = (place) => !!place && Array.isArray(place.cities) && place.cities.length > 1;

const CityPicker = () => {
    const { cities, city, myCity, needsMyCity, ready, setCity, pickerOpen, closePicker } = useContext(CityContext);
    const location = useLocation();
    const [locating, setLocating] = useState(false);
    const [msg, setMsg] = useState('');
    // מרחב שנבחר מהרשימה ומחכה לבחירת עיר
    const [region, setRegion] = useState(null);

    const canGate = ready && cities.length > 0 && !isBot()
        && !NO_GATE_PATHS.some(p => location.pathname.startsWith(p));
    const gate = canGate && (!city || needsMyCity);
    const open = gate || (pickerOpen && cities.length > 0);
    if (!open) return null;

    // שלב העיר: מרחב שנבחר עכשיו, או המרחב השמור שעוד אין לו עיר
    const step2 = region || (needsMyCity ? city : null);

    const pickPlace = (place) => {
        setMsg('');
        if (hasManyCities(place)) setRegion(place);
        else setCity(place);
    };

    const pickCity = (c) => {
        setCity(step2, c);
        setRegion(null);
    };

    const close = () => {
        setRegion(null);
        closePicker();
    };

    const detect = async () => {
        setLocating(true);
        setMsg('');
        try {
            const pos = await withTimeout(utilsCities.getPosition(10000), 20000);
            const res = await utilsCities.locate(pos.lat, pos.lng);
            if (res && res.city) {
                setCity(res.city, res.myCity);
                setRegion(null);
                return;
            }
            setMsg(FAIL_MSG.outside);
        } catch (e) {
            setMsg(FAIL_MSG[e && e.reason] || FAIL_MSG.error);
        } finally {
            setLocating(false);
        }
    };

    const canClose = !!city && !needsMyCity;

    if (step2) {
        return (
            <div className="cityPickerOverlay" dir="rtl" role="dialog" aria-modal="true" aria-label="בחירת עיר">
                <div className="cityPickerBox">
                    {canClose &&
                        <button type="button" className="cityPickerClose" aria-label="סגור" onClick={close}>
                            <Close />
                        </button>}
                    <div className="cityPickerTitle">באיזו עיר ב{step2.name}?</div>
                    <div className="cityPickerSub">כדי שנציג לכם קודם את מי שמגיע אליכם</div>

                    <button type="button" className="cityPickerLocate" disabled={locating} onClick={detect}>
                        <MyLocation className="cityPickerLocateIcon" />
                        {locating ? 'מזהה מיקום...' : 'זהה לפי מיקום'}
                    </button>

                    {msg && <div className="cityPickerMsg" role="alert">{msg}</div>}

                    <div className="cityPickerOr"><span>או בחרו מהרשימה</span></div>

                    <div className="cityPickerList">
                        {step2.cities.map(c => (
                            <button key={c._id} type="button"
                                className={`cityPickerCity ${myCity && String(myCity._id) === String(c._id) ? 'isSelected' : ''}`}
                                onClick={() => pickCity(c)}>
                                <LocationOn className="cityPickerCityIcon" />
                                {c.name}
                            </button>
                        ))}
                    </div>

                    <button type="button" className="cityPickerBack" onClick={() => { setRegion(null); setMsg(''); if (needsMyCity) setCity(null); }}>
                        חזרה לרשימת האזורים
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="cityPickerOverlay" dir="rtl" role="dialog" aria-modal="true" aria-label="בחירת עיר">
            <div className="cityPickerBox">
                {canClose &&
                    <button type="button" className="cityPickerClose" aria-label="סגור" onClick={close}>
                        <Close />
                    </button>}
                <div className="cityPickerTitle">{city ? 'החלפת עיר' : 'באיזו עיר אתם?'}</div>
                <div className="cityPickerSub">נציג לכם קודם את העסקים של העיר שלכם</div>

                <button type="button" className="cityPickerLocate" disabled={locating} onClick={detect}>
                    <MyLocation className="cityPickerLocateIcon" />
                    {locating ? 'מזהה מיקום...' : city ? 'זהה לפי מיקום' : 'שתף מיקום'}
                </button>

                {msg && <div className="cityPickerMsg" role="alert">{msg}</div>}

                <div className="cityPickerOr"><span>או בחרו מהרשימה</span></div>

                <div className="cityPickerList">
                    {cities.map(c => (
                        <button key={c._id} type="button"
                            className={`cityPickerCity ${city && String(city._id) === String(c._id) ? 'isSelected' : ''}`}
                            onClick={() => pickPlace(c)}>
                            <LocationOn className="cityPickerCityIcon" />
                            {c.name}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

// כפתור העיר בכותרת / בתפריט
export const CityChip = ({ className = '', onClick }) => {
    const { cities, city, myCity, openPicker } = useContext(CityContext);
    if (!cities.length) return null;
    // העיר בפועל (גם כשהמקום הוא מרחב)
    const label = myCity ? myCity.name : city ? city.name : '';
    return (
        <button type="button" className={`cityChip ${className}`}
            onClick={() => { openPicker(); onClick && onClick(); }}
            aria-label={label ? `העיר: ${label}. לחצו להחלפה` : 'בחירת עיר'}>
            <LocationOn className="cityChipIcon" />
            <span>{label || 'בחר עיר'}</span>
            <span className="cityChipArrow">▾</span>
        </button>
    );
};

export default CityPicker;
